// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Higher-level hook for running operations that return job IDs.
 * Automatically tracks job completion via polling.
 *
 * Why this exists: Apps previously had to manually wire useJobPolling
 * with their API calls, leading to duplicated glue code and bugs
 * (e.g., VersionPanel forgot to enqueue jobs for polling).
 */
import { useCallback, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'
import { useJobPolling } from './useJobPolling'

interface UseTrackedOperationParams {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  /** Polling interval in ms (default: 2500) */
  pollIntervalMs?: number
  /** Called when any job status changes */
  onStatusChange?: () => Promise<void> | void
  /** Called when all tracked jobs reach a terminal state. Summary lists errored job ids. */
  onAllComplete?: (summary: {
    erroredJobIds: string[]
    jobStatuses: Record<string, string>
  }) => void
}

interface UseTrackedOperationResult {
  /**
   * Run an operation and automatically track returned job IDs.
   *
   * @param operation - Async function that performs the action
   * @param extractJobIds - Function to extract job IDs from the result
   * @returns The operation result (pass-through)
   *
   * @example
   * ```tsx
   * const result = await runTracked(
   *   () => apiClient.post('/run', { documentIds }),
   *   (response) => response.data.jobs?.map(j => j.jobId).filter(Boolean) ?? []
   * )
   * ```
   */
  runTracked: <T>(
    operation: () => Promise<T>,
    extractJobIds: (result: T) => string[]
  ) => Promise<T>
  /** Whether an operation is currently in flight */
  isRunning: boolean
  /** Currently tracked job IDs being polled */
  activeJobs: string[]
  /** Latest known status for every tracked job (including terminal statuses) */
  jobStatuses: Record<string, string>
  /** Manually add job IDs to track (e.g. resuming jobs from persisted state) */
  enqueueJobs: (jobIds: string[]) => void
}

/**
 * Run async operations and automatically poll for job completion.
 *
 * Composes useJobPolling internally - apps don't need to wire up polling manually.
 *
 * @example
 * ```tsx
 * const { runTracked, isRunning, activeJobs } = useTrackedOperation({
 *   apiClient,
 *   orgId: 'umd-org',
 *   workspaceId: 'ora-contracts',
 *   onStatusChange: () => refetchData(),
 *   onAllComplete: () => setMessage('All jobs completed'),
 * })
 *
 * // Run pipeline - polling starts automatically
 * await runTracked(
 *   () => apiClient.post('/run', { documentIds, pipelineId }),
 *   (res) => res.data.jobs?.map(j => j.jobId).filter(Boolean) ?? []
 * )
 *
 * // Run version comparison - same pattern
 * await runTracked(
 *   () => apiClient.post('/run', { documentIds, pipelineId: 'version-diff', compareVersions }),
 *   (res) => res.data.jobs?.map(j => j.jobId).filter(Boolean) ?? []
 * )
 * ```
 */
export function useTrackedOperation({
  apiClient,
  orgId,
  workspaceId,
  pollIntervalMs,
  onStatusChange,
  onAllComplete,
}: UseTrackedOperationParams): UseTrackedOperationResult {
  const [isRunning, setIsRunning] = useState(false)

  const { enqueueJobs, activeJobIds, jobStatuses } = useJobPolling({
    apiClient,
    orgId,
    workspaceId,
    pollIntervalMs,
    onStatusChange,
    onAllComplete,
  })

  const runTracked = useCallback(
    async <T>(
      operation: () => Promise<T>,
      extractJobIds: (result: T) => string[]
    ): Promise<T> => {
      setIsRunning(true)
      try {
        const result = await operation()
        const jobIds = extractJobIds(result)
        if (jobIds.length > 0) {
          enqueueJobs(jobIds)
        }
        return result
      } finally {
        setIsRunning(false)
      }
    },
    [enqueueJobs]
  )

  return {
    runTracked,
    isRunning,
    activeJobs: activeJobIds,
    jobStatuses,
    enqueueJobs,
  }
}
